/**
 * API Prefetch Module
 *
 * This module fires critical API requests as early as possible — ideally
 * before the main JS bundles finish evaluating — so that network I/O
 * overlaps with JS parse/compile time instead of happening sequentially.
 *
 * The inline script in index.html calls `window.__prefetch(...)` to start
 * fetches immediately on HTML parse. This module then exposes helpers for
 * the React app to consume the prefetched responses instead of making
 * duplicate requests.
 *
 * Timeline improvement:
 *   Before: [HTML] → [JS parse ~500ms] → [partnerWeb] → [Login] → [styles] → ...
 *   After:  [HTML] → [JS parse ~500ms]  ← parallel →  [partnerWeb, Login, styles already done]
 */

export interface PrefetchEntry {
	url: string;
	promise: Promise<Response>;
}

declare global {
	interface Window {
		__prefetchCache?: Map<string, PrefetchEntry>;
		__prefetch?: (
			urls: Array<{
				url: string;
				method?: string;
				credentials?: RequestCredentials;
			}>
		) => void;
	}
}

/**
 * Normalize a URL by sorting its query parameters alphabetically.
 * This ensures cache lookups match regardless of query param ordering.
 */
function normalizeUrl(url: string): string {
	try {
		const parsed = new URL(url, window.location.origin);
		parsed.searchParams.sort()
		parsed.search = parsed.searchParams.toString();
		return parsed.pathname + (parsed.search ? "?" + parsed.search : "");
	} catch {
		return url;
	}
}

/**
 * Retrieve a prefetched response for a given URL.
 * Returns the Response promise if the URL was prefetched, or undefined.
 * Each prefetched entry can only be consumed once to avoid stale data issues.
 */
export function consumePrefetch(url: string): Promise<Response> | undefined {
	const cache = window.__prefetchCache;
	if (!cache || cache.size === 0) return undefined;

	// Try exact match first
	let entry = cache.get(url);
	if (entry) {
		// Remove all keys pointing to this entry
		cache.forEach((val, key) => {
			if (val === entry) cache.delete(key);
		});
		return entry.promise;
	}

	// Try matching with/without origin
	try {
		const fullUrl = new URL(url, window.location.origin).href;
		entry = cache.get(fullUrl);
		if (!entry) {
			// Try the pathname + search portion
			const parsed = new URL(url, window.location.origin);
			entry = cache.get(parsed.pathname + parsed.search);
		}
	} catch {
		// URL parsing failed, no match
	}

	if (entry) {
		cache.forEach((val, key) => {
			if (val === entry) cache.delete(key);
		});
		return entry.promise;
	}

	// Fallback: normalize both sides (sort query params) and compare.
	// This handles cases where the prefetch and React build the same URL
	// with query params in a different order.
	const normalizedUrl = normalizeUrl(url);
	let normalizedEntry: PrefetchEntry | undefined;
	cache.forEach((candidate, key) => {
		if (!normalizedEntry && normalizeUrl(key) === normalizedUrl) {
			normalizedEntry = candidate;
		}
	});

	if (normalizedEntry) {
		cache.forEach((val, key) => {
			if (val === normalizedEntry) cache.delete(key);
		});
		return normalizedEntry.promise;
	}

	return undefined;
}

/**
 * Check if any prefetched responses are available.
 */
export function hasPrefetchedData(): boolean {
	return (window.__prefetchCache?.size ?? 0) > 0;
}

/**
 * Install the prefetch bootstrap function on window.
 * This is called from the inline script in index.html.
 * If the inline script runs before this module loads (which is the point),
 * it will set up __prefetchCache directly. This function serves as a
 * fallback / initialization path.
 */
export function initPrefetch(): void {
	if (!window.__prefetchCache) {
		window.__prefetchCache = new Map();
	}
}
